import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, Link } from 'react-router-dom'
import { workOrdersApi, rapportiniApi, riordinoApi, assegnazioniCantiereApi } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { format } from 'date-fns'
import { it } from 'date-fns/locale'
import { MapPin, Phone, Play, ChevronRight, CalendarX, RefreshCw, ClipboardList, Navigation, X, AlertTriangle, CheckCircle, ExternalLink, Home, PauseCircle } from 'lucide-react'
import { AppuntamentoChip } from '../components/AppuntamentoChip'
import toast from 'react-hot-toast'
import ConsegnaMezzoModal from '../components/ConsegnaMezzoModal'
import TerminaAttivitaModal from '../components/TerminaAttivitaModal'

const STATO_COLORS: Record<string, { bg: string; color: string; label: string }> = {
  ricevuto:  { bg: '#dbeafe', color: '#1d4ed8', label: 'Ricevuto' },
  assegnato: { bg: '#fef9c3', color: '#854d0e', label: 'Assegnato' },
  sospeso:   { bg: '#f3f4f6', color: '#374151', label: 'Sospeso' },
  annullato: { bg: '#fee2e2', color: '#b91c1c', label: 'Annullato' },
  eseguito:  { bg: '#dcfce7', color: '#15803d', label: 'Eseguito' },
}
export default function MioTurno() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const qc = useQueryClient()

  if (!['tecnico', 'tecnico_senior'].includes(user?.role ?? '')) {
    navigate('/', { replace: true })
    return null
  }
  const oggi = format(new Date(), 'yyyy-MM-dd')
  const oggiLabel = format(new Date(), 'EEEE d MMMM yyyy', { locale: it })

  // Il backend auto-scopal per tecnico/tecnico_senior — basta passare anno+mese
  const anno = new Date().getFullYear()
  const mese = new Date().getMonth() + 1

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['mio-turno', oggi],
    queryFn: () => workOrdersApi.list({ anno, mese, per_page: 100 }).then(r => r.data),
    staleTime: 60_000,
  })

  const [modalConsegna, setModalConsegna] = useState<'presa' | 'riconsegna' | null>(null)
  const [modalTerminaAttivita, setModalTerminaAttivita] = useState(false)

  const { data: mieAssegnazioniData, refetch: refetchMiaAssegnazione } = useQuery({
    queryKey: ['mia-assegnazione-oggi', oggi],
    queryFn: () => assegnazioniCantiereApi.list({ data_da: oggi, data_a: oggi }).then(r => r.data),
  })
  const miaAssegnazioneOggi = (mieAssegnazioniData || [])[0]

  // Filtra client-side per la data odierna
  const allItems: any[] = data?.items ?? []
  const pratiche = allItems
    .filter((wo: any) => {
      if (!wo.data_appuntamento) return false
      return wo.data_appuntamento.slice(0, 10) === oggi
    })
    .sort((a: any, b: any) => {
      // Ordine giro esplicito ha precedenza
      const ao = a.ordine_giro, bo = b.ordine_giro
      if (ao != null && bo != null) return ao - bo
      if (ao != null) return -1
      if (bo != null) return 1
      // Fallback: slot orario
      const slotKey = (s?: string | null): string => {
        if (!s) return '99:99'
        const upper = s.toUpperCase()
        if (upper === 'AM') return '08:00'
        if (upper === 'PM') return '13:00'
        const match = s.match(/^(\d{2}:\d{2})/)
        return match ? match[1] : '99:99'
      }
      return slotKey(a.slot_orario).localeCompare(slotKey(b.slot_orario))
    })

  const avviaMut = useMutation({
    mutationFn: (id: number) => workOrdersApi.updateStato(id, 'assegnato'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mio-turno'] })
      qc.invalidateQueries({ queryKey: ['work-orders'] })
      toast.success('Intervento avviato ✓')
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || 'Errore avvio intervento'),
  })

  // ── Sospendi modal ────────────────────────────────────────────
  const [sospendiWo, setSospendiWo] = useState<any | null>(null)
  const [sospendiMotivo, setSospendiMotivo] = useState('')
  const [sospendiDirettore, setSospendiDirettore] = useState('')

  const sospendiMut = useMutation({
    mutationFn: async (wo: any) => {
      const oggi_str = format(new Date(), 'yyyy-MM-dd')
      const noteText = `INTERVENTO SOSPESO\n\nMotivo: ${sospendiMotivo.trim()}\nReferente/Direttore: ${sospendiDirettore.trim() || '—'}`
      await workOrdersApi.updateStato(wo.id, 'sospeso', sospendiMotivo.trim())
      // cerca rapportino esistente per questo WO
      const existing = await rapportiniApi.list({ work_order_id: wo.id, per_page: 1 })
      const items: any[] = existing.data?.items ?? []
      if (items.length > 0) {
        await rapportiniApi.update(items[0].id, {
          note_aggiuntive: noteText,
          esito: 'non_completato',
          causale_non_completamento: 'altro',
        })
      } else {
        await rapportiniApi.create({
          work_order_id: wo.id,
          data_intervento: oggi_str,
          esito: 'non_completato',
          causale_non_completamento: 'altro',
          note_aggiuntive: noteText,
          firma_cliente_nome: sospendiDirettore.trim() || undefined,
        })
      }
    },
    onSuccess: () => {
      toast.success('Pratica sospesa e rapportino registrato')
      qc.invalidateQueries({ queryKey: ['mio-turno'] })
      qc.invalidateQueries({ queryKey: ['work-orders'] })
      setSospendiWo(null)
      setSospendiMotivo('')
      setSospendiDirettore('')
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || 'Errore sospensione'),
  })

  // ── Percorso Ottimale ──────────────────────────────────────────
  const [percorsoOpen, setPercorsoOpen] = useState(false)
  const [percorsoGps, setPercorsoGps] = useState<{ lat: number; lng: number } | null>(null)
  const [percorsoGpsFallback, setPercorsoGpsFallback] = useState(false) // true = centroide pratiche, non GPS reale
  const [percorsoStatus, setPercorsoStatus] = useState<'idle' | 'loading' | 'ready' | 'no_gps'>('idle')
  const [percorsoTab, setPercorsoTab] = useState<'attuale' | 'ottimale'>('ottimale')
  const [notaTecnico, setNotaTecnico] = useState('')
  const [proponiStatus, setProponiStatus] = useState<'idle' | 'sending' | 'sent'>('idle')

  const haversine = (lat1: number, lng1: number, lat2: number, lng2: number) => {
    const R = 6371
    const dLat = (lat2 - lat1) * Math.PI / 180
    const dLon = (lng2 - lng1) * Math.PI / 180
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  }

  const fmtHM = (d: Date) => d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })

  // Calcola steps per un ordine dato
  const calcolaSteps = (gps: { lat: number; lng: number }, orderedWos: any[]) => {
    const VELOCITA_KMH = 50
    const DURATA_MIN = 35
    let now = new Date()
    let curLat = gps.lat
    let curLng = gps.lng

    const steps = orderedWos.map((wo: any) => {
      let distanza_km: number | null = null
      let tempo_viaggio_min: number | null = null
      let ora_arrivo: Date
      if (wo.lat != null && wo.lng != null) {
        distanza_km = haversine(curLat, curLng, wo.lat, wo.lng)
        tempo_viaggio_min = Math.ceil((distanza_km / VELOCITA_KMH) * 60)
        ora_arrivo = new Date(now.getTime() + tempo_viaggio_min * 60_000)
        curLat = wo.lat; curLng = wo.lng
      } else {
        ora_arrivo = new Date(now.getTime())
      }
      const ora_fine = new Date(ora_arrivo.getTime() + DURATA_MIN * 60_000)
      now = ora_fine

      let chiusura_ok: boolean | null = null
      let margine_min: number | null = null
      if (wo.ora_chiusura) {
        const [ch, cm] = wo.ora_chiusura.split(':').map(Number)
        const chiusura = new Date(ora_fine)
        chiusura.setHours(ch, cm, 0, 0)
        margine_min = Math.floor((chiusura.getTime() - ora_fine.getTime()) / 60_000)
        chiusura_ok = margine_min >= 0
      }
      return { wo, distanza_km, tempo_viaggio_min, ora_arrivo, ora_fine, chiusura_ok, margine_min }
    })

    // Ritorno
    let ritorno: { distanza_km: number; tempo_viaggio_min: number; ora_arrivo: Date } | null = null
    if (orderedWos.some((w: any) => w.lat != null)) {
      const d = haversine(curLat, curLng, gps.lat, gps.lng)
      const t = Math.ceil((d / VELOCITA_KMH) * 60)
      ritorno = { distanza_km: d, tempo_viaggio_min: t, ora_arrivo: new Date(now.getTime() + t * 60_000) }
    }
    // Totale km percorsi
    const totKm = steps.reduce((s: number, st: any) => s + (st.distanza_km ?? 0), 0) + (ritorno?.distanza_km ?? 0)
    return { steps, ritorno, totKm }
  }

  // Nearest-neighbor TSP a partire dal GPS
  const ottimizzaOrdine = (gps: { lat: number; lng: number }, wos: any[]): any[] => {
    const withCoords = wos.filter((w: any) => w.lat != null && w.lng != null)
    const withoutCoords = wos.filter((w: any) => w.lat == null || w.lng == null)
    let remaining = [...withCoords]
    const ordered: any[] = []
    let curLat = gps.lat; let curLng = gps.lng
    while (remaining.length > 0) {
      let minD = Infinity; let nearest = remaining[0]; let idx = 0
      remaining.forEach((w, i) => {
        const d = haversine(curLat, curLng, w.lat, w.lng)
        if (d < minD) { minD = d; nearest = w; idx = i }
      })
      ordered.push(nearest)
      curLat = nearest.lat; curLng = nearest.lng
      remaining.splice(idx, 1)
    }
    return [...ordered, ...withoutCoords]
  }

  // Centroide delle pratiche con coordinate — usato come fallback quando GPS non disponibile (HTTP)
  const calcolaCentroide = (): { lat: number; lng: number } | null => {
    const withCoords = pratiche.filter((w: any) => w.lat != null && w.lng != null)
    if (withCoords.length === 0) return null
    const lat = withCoords.reduce((s: number, w: any) => s + w.lat, 0) / withCoords.length
    const lng = withCoords.reduce((s: number, w: any) => s + w.lng, 0) / withCoords.length
    return { lat, lng }
  }

  const apriPercorso = () => {
    setPercorsoOpen(true)
    setPercorsoStatus('loading')
    setPercorsoGps(null)
    setPercorsoGpsFallback(false)
    setProponiStatus('idle')
    setNotaTecnico('')
    setPercorsoTab('ottimale')

    const usaFallback = () => {
      const centro = calcolaCentroide()
      if (centro) {
        setPercorsoGps(centro)
        setPercorsoGpsFallback(true)
        setPercorsoStatus('ready')
      } else {
        setPercorsoStatus('no_gps')
      }
    }

    if (!navigator.geolocation) { usaFallback(); return }

    try {
      navigator.geolocation.getCurrentPosition(
        pos => {
          setPercorsoGpsFallback(false)
          setPercorsoGps({ lat: pos.coords.latitude, lng: pos.coords.longitude })
          setPercorsoStatus('ready')
        },
        () => usaFallback(),
        { enableHighAccuracy: true, timeout: 8000 }
      )
    } catch {
      usaFallback()
    }
  }

  const mapsUrl = (gps: { lat: number; lng: number }, orderedWos: any[]) => {
    const pts = orderedWos.filter((w: any) => w.lat != null).map((w: any) => `${w.lat},${w.lng}`)
    if (pts.length === 0) return null
    const origin = `${gps.lat},${gps.lng}`
    const dest = pts[pts.length - 1]
    const wps = pts.slice(0, -1)
    const base = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}&travelmode=driving`
    return wps.length > 0 ? `${base}&waypoints=${wps.join('|')}` : base
  }

  const proponiRiordino = async (ottimaleOrdinato: any[]) => {
    setProponiStatus('sending')
    try {
      const ordini = ottimaleOrdinato.map((wo: any, i: number) => ({
        wo_id: wo.id,
        numero_progressivo: wo.numero_progressivo,
        denominazione: wo.denominazione_sito || wo.cliente_comune || undefined,
        ordine_corrente: wo.ordine_giro ?? undefined,
        ordine_proposto: i + 1,
      }))
      await riordinoApi.proponi({
        data_turno: oggi,
        ordini,
        nota_tecnico: notaTecnico || undefined,
      })
      setProponiStatus('sent')
    } catch {
      setProponiStatus('idle')
      toast.error('Errore invio proposta')
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5', display: 'flex', flexDirection: 'column' }}>

      {miaAssegnazioneOggi && (
        <div className="card p-4 mb-4" style={{ margin: 12 }}>
          <h3 className="font-semibold mb-2">📅 Assegnazione di oggi</h3>
          <p className="text-sm">{miaAssegnazioneOggi.commessa_numero} - {miaAssegnazioneOggi.commessa_nome}</p>
          <p className="text-sm">{miaAssegnazioneOggi.cantiere_nome}, {miaAssegnazioneOggi.cantiere_indirizzo}</p>
          {miaAssegnazioneOggi.ora_inizio && <p className="text-sm">Orario: {miaAssegnazioneOggi.ora_inizio}</p>}
          {miaAssegnazioneOggi.mezzo_targa && (
            <p className="text-sm">Mezzo: {miaAssegnazioneOggi.mezzo_targa} ({miaAssegnazioneOggi.mezzo_marca} {miaAssegnazioneOggi.mezzo_modello})</p>
          )}
          {miaAssegnazioneOggi.mezzo_id && !miaAssegnazioneOggi.data_presa_consegna && (
            <button className="btn-primary mt-2" onClick={() => setModalConsegna('presa')}>
              Conferma presa in consegna
            </button>
          )}
          {miaAssegnazioneOggi.mezzo_id && miaAssegnazioneOggi.data_presa_consegna && !miaAssegnazioneOggi.data_riconsegna && (
            <button className="btn-secondary mt-2" onClick={() => setModalConsegna('riconsegna')}>
              Dichiara riconsegna
            </button>
          )}
          {(miaAssegnazioneOggi.status === 'assegnato' || miaAssegnazioneOggi.status === 'in_corso') && (
            <button
              className="btn-primary mt-2"
              onClick={() => setModalTerminaAttivita(true)}
            >
              Termina attività
            </button>
          )}
        </div>
      )}

      {modalConsegna && miaAssegnazioneOggi && (
        <ConsegnaMezzoModal
          assegnazioneId={miaAssegnazioneOggi.id}
          modalita={modalConsegna}
          kmMinimi={modalConsegna === 'riconsegna' ? miaAssegnazioneOggi.km_presa_consegna : undefined}
          onClose={() => setModalConsegna(null)}
          onSuccess={() => {
            setModalConsegna(null)
            refetchMiaAssegnazione()
            qc.invalidateQueries({ queryKey: ['mia-assegnazione-oggi'] })
            qc.invalidateQueries({ queryKey: ['mio-turno'] })
          }}
        />
      )}

      {modalTerminaAttivita && miaAssegnazioneOggi && (
        <TerminaAttivitaModal
          assegnazioneId={miaAssegnazioneOggi.id}
          commessaNome={`${miaAssegnazioneOggi.commessa_numero} - ${miaAssegnazioneOggi.commessa_nome}`}
          onClose={() => setModalTerminaAttivita(false)}
          onSuccess={() => {
            setModalTerminaAttivita(false)
            refetchMiaAssegnazione()
            qc.invalidateQueries({ queryKey: ['mia-assegnazione-oggi'] })
            qc.invalidateQueries({ queryKey: ['mio-turno'] })
          }}
        />
      )}

      {/* ── Header fisso ───────────────────────────────────────── */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: '#1E40AF',
        padding: '12px 16px 10px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>
              XSophia
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 1 }}>
              Il Mio Turno
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>
              {user?.nome} {user?.cognome}
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.70)', marginTop: 1, textTransform: 'capitalize' }}>
              {oggiLabel}
            </div>
          </div>
        </div>
      </header>

      {/* ── Corpo ──────────────────────────────────────────────── */}
      <main style={{ flex: 1, padding: '16px 12px 80px' }}>

        {/* Barra info + refresh */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
            {isLoading ? 'Caricamento...' : `${pratiche.length} intervento${pratiche.length !== 1 ? 'i' : ''} oggi`}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {pratiche.length > 0 && (
              <button
                onClick={apriPercorso}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  fontSize: 13, fontWeight: 700, color: '#fff',
                  background: '#1E40AF', border: 'none',
                  cursor: 'pointer', padding: '8px 12px', borderRadius: 8,
                  minHeight: 44, boxShadow: '0 1px 4px rgba(30,64,175,0.25)',
                }}
              >
                <Navigation size={15} /> Percorso
              </button>
            )}
            <button
              onClick={() => refetch()}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                fontSize: 13, color: '#1E40AF', background: 'none', border: 'none',
                cursor: 'pointer', padding: '6px 10px', borderRadius: 8,
                minHeight: 44,
              }}
            >
              <RefreshCw size={15} /> Aggiorna
            </button>
          </div>
        </div>

        {/* Loading */}
        {isLoading && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', paddingTop: 80, gap: 12 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', border: '3px solid #dbeafe', borderTopColor: '#1E40AF', animation: 'spin 0.7s linear infinite' }} />
            <span style={{ fontSize: 14, color: '#6b7280' }}>Caricamento interventi...</span>
            <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
          </div>
        )}

        {/* Nessuna pratica */}
        {!isLoading && pratiche.length === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', paddingTop: 80, gap: 16, textAlign: 'center' }}>
            <CalendarX size={56} color="#d1d5db" />
            <div>
              <p style={{ fontSize: 17, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Nessun intervento programmato</p>
              <p style={{ fontSize: 14, color: '#9ca3af' }}>Non hai interventi assegnati per oggi.</p>
            </div>
            <Link to="/pratiche" style={{ marginTop: 8, padding: '12px 24px', borderRadius: 12, background: '#1E40AF', color: '#fff', fontWeight: 600, fontSize: 15, textDecoration: 'none', minHeight: 44, display: 'flex', alignItems: 'center' }}>
              Vedi tutte le pratiche
            </Link>
          </div>
        )}

        {/* Lista pratiche */}
        {!isLoading && pratiche.map((wo: any) => {
          const stato = STATO_COLORS[wo.stato] ?? { bg: '#f3f4f6', color: '#374151', label: wo.stato }
          const indirizzo = [wo.cliente_indirizzo, wo.cliente_comune].filter(Boolean).join(', ')
          const mapsUrl = wo.cliente_lat && wo.cliente_lng
            ? `https://www.google.com/maps?q=${wo.cliente_lat},${wo.cliente_lng}`
            : `https://www.google.com/maps?q=${encodeURIComponent(indirizzo)}`
          const isTerminato = wo.stato === 'eseguito' || wo.stato === 'annullato'

          return (
            <div key={wo.id} style={{
              background: '#fff',
              borderRadius: 16,
              marginBottom: 12,
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              overflow: 'hidden',
              border: '1px solid #e5e7eb',
            }}>
              {/* Card header */}
              <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 15, fontWeight: 700, color: '#1E40AF', letterSpacing: '-0.01em' }}>
                        {wo.numero_progressivo}
                      </div>
                      {wo.ordine_giro != null && (
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', flexShrink: 0 }}>
                          #{wo.ordine_giro}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {wo.denominazione_sito || wo.cliente_nome || '—'}
                    </div>
                  </div>
                  <span style={{ flexShrink: 0, padding: '4px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600, background: stato.bg, color: stato.color }}>
                    {stato.label}
                  </span>
                </div>
              </div>

              {/* Card body */}
              <div style={{ padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>

                <AppuntamentoChip wo={wo} />

                {/* Tipologia + Slot */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {wo.tipologia && (
                    <span style={{ fontSize: 12, padding: '3px 8px', background: '#f0f2f5', borderRadius: 6, color: '#374151', textTransform: 'capitalize' }}>
                      {wo.tipologia.replace(/_/g, ' ')}
                    </span>
                  )}
                </div>

                {/* Indirizzo → Maps */}
                {indirizzo && (
                  <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 8,
                      color: '#1E40AF', textDecoration: 'none',
                      fontSize: 14, lineHeight: 1.4,
                      minHeight: 44, padding: '4px 0',
                    }}
                  >
                    <MapPin size={16} style={{ flexShrink: 0, marginTop: 2, color: '#F97316' }} />
                    <span style={{ flex: 1 }}>{indirizzo}</span>
                    <ChevronRight size={16} style={{ flexShrink: 0, opacity: 0.4, marginTop: 2 }} />
                  </a>
                )}

                {/* Telefono cliente */}
                {wo.cliente_telefono && (
                  <a
                    href={`tel:${wo.cliente_telefono}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      color: '#1E40AF', textDecoration: 'none',
                      fontSize: 14, minHeight: 44, padding: '4px 0',
                    }}
                  >
                    <Phone size={16} style={{ flexShrink: 0, color: '#6b7280' }} />
                    <span>{wo.cliente_telefono}</span>
                  </a>
                )}
              </div>

              {/* Card footer — azioni */}
              {!isTerminato && (
                <div style={{ padding: '10px 16px 14px', display: 'flex', gap: 10 }}>
                  {wo.stato === 'ricevuto' && (
                    <button
                      onClick={() => avviaMut.mutate(wo.id)}
                      disabled={avviaMut.isPending}
                      style={{
                        flex: 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        minHeight: 48, borderRadius: 12,
                        background: '#16a34a', color: '#fff',
                        fontWeight: 700, fontSize: 15, border: 'none', cursor: 'pointer',
                        opacity: avviaMut.isPending ? 0.7 : 1,
                      }}
                    >
                      <Play size={16} fill="#fff" /> {avviaMut.isPending ? 'Avvio...' : 'Avvia intervento'}
                    </button>
                  )}
                  <button
                    onClick={() => { setSospendiWo(wo); setSospendiMotivo(''); setSospendiDirettore('') }}
                    style={{
                      flex: '0 0 auto',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      minHeight: 48, borderRadius: 12, padding: '0 14px',
                      background: '#fef3c7', color: '#92400e',
                      fontWeight: 700, fontSize: 14, border: '1.5px solid #f59e0b', cursor: 'pointer',
                    }}
                  >
                    <PauseCircle size={15} /> Sospendi
                  </button>
                  <button
                    onClick={() => navigate(`/pratiche/${wo.id}`)}
                    style={{
                      flex: 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      minHeight: 48, borderRadius: 12,
                      background: '#1E40AF', color: '#fff',
                      fontWeight: 700, fontSize: 15, border: 'none', cursor: 'pointer',
                    }}
                  >
                    Completa <ChevronRight size={16} />
                  </button>
                </div>
              )}
              {isTerminato && (
                <div style={{ padding: '8px 16px 12px' }}>
                  <button
                    onClick={() => navigate(`/pratiche/${wo.id}`)}
                    style={{
                      width: '100%', minHeight: 44, borderRadius: 12,
                      background: '#f3f4f6', color: '#374151',
                      fontWeight: 600, fontSize: 14, border: 'none', cursor: 'pointer',
                    }}
                  >
                    Visualizza dettaglio
                  </button>
                </div>
              )}
            </div>
          )
        })}
      </main>

      {/* ── Modale Sospendi ──────────────────────────────────────── */}
      {sospendiWo && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 440, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <PauseCircle size={22} color="#d97706" />
              <span style={{ fontWeight: 700, fontSize: 17 }}>Sospendi intervento</span>
              <button onClick={() => setSospendiWo(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}><X size={20} /></button>
            </div>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 18 }}>{sospendiWo.numero_progressivo} — {sospendiWo.titolo || sospendiWo.tipologia}</p>

            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Motivo sospensione <span style={{ color: '#dc2626' }}>*</span></label>
            <textarea
              rows={3}
              value={sospendiMotivo}
              onChange={e => setSospendiMotivo(e.target.value)}
              placeholder="Descrivere il motivo della sospensione (obbligatorio)..."
              style={{ width: '100%', borderRadius: 8, border: '1.5px solid #d1d5db', padding: '8px 10px', fontSize: 14, resize: 'vertical', boxSizing: 'border-box', marginBottom: 14 }}
            />

            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Nome e cognome referente / direttore</label>
            <input
              type="text"
              value={sospendiDirettore}
              onChange={e => setSospendiDirettore(e.target.value)}
              placeholder="Es. Mario Rossi"
              style={{ width: '100%', borderRadius: 8, border: '1.5px solid #d1d5db', padding: '8px 10px', fontSize: 14, boxSizing: 'border-box', marginBottom: 20 }}
            />

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setSospendiWo(null)}
                style={{ flex: 1, minHeight: 44, borderRadius: 10, background: '#f3f4f6', color: '#374151', fontWeight: 600, fontSize: 14, border: 'none', cursor: 'pointer' }}
              >Annulla</button>
              <button
                onClick={() => {
                  if (!sospendiMotivo.trim()) { toast.error('Il motivo della sospensione è obbligatorio'); return }
                  sospendiMut.mutate(sospendiWo)
                }}
                disabled={sospendiMut.isPending || !sospendiMotivo.trim()}
                style={{ flex: 2, minHeight: 44, borderRadius: 10, background: '#f59e0b', color: '#fff', fontWeight: 700, fontSize: 15, border: 'none', cursor: !sospendiMotivo.trim() ? 'not-allowed' : 'pointer', opacity: sospendiMut.isPending || !sospendiMotivo.trim() ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              >
                <PauseCircle size={16} /> {sospendiMut.isPending ? 'Sospensione...' : 'Conferma sospensione'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modale Percorso Ottimale ──────────────────────────────── */}
      {percorsoOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 200,
          background: 'rgba(0,0,0,0.55)', display: 'flex', flexDirection: 'column',
          alignItems: 'stretch',
        }}
          onClick={e => { if (e.target === e.currentTarget) setPercorsoOpen(false) }}
        >
          <div style={{
            position: 'absolute', inset: 0, overflowY: 'auto',
            display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px 12px 40px',
          }}>
            <div style={{
              background: '#fff', borderRadius: 20, width: '100%', maxWidth: 480,
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)', overflow: 'hidden',
            }}>
              {/* Intestazione modale */}
              <div style={{ background: '#1E40AF', padding: '16px 16px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 17, fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Navigation size={18} /> Percorso Ottimale
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>
                    {oggiLabel} · 35 min/intervento · 50 km/h
                  </div>
                </div>
                <button onClick={() => setPercorsoOpen(false)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, padding: 8, cursor: 'pointer', color: '#fff' }}>
                  <X size={18} />
                </button>
              </div>

              {/* Corpo */}
              <div style={{ padding: '16px 16px 20px' }}>

                {/* Stato GPS loading */}
                {percorsoStatus === 'loading' && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '32px 0', gap: 12 }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', border: '3px solid #dbeafe', borderTopColor: '#1E40AF', animation: 'spin 0.7s linear infinite' }} />
                    <span style={{ fontSize: 14, color: '#6b7280' }}>Acquisizione posizione GPS...</span>
                  </div>
                )}

                {/* GPS non disponibile — mostra lista ordinata con link Maps per indirizzo */}
                {percorsoStatus === 'no_gps' && (() => {
                  const mapsAddr = (wo: any) => {
                    if (wo.cliente_lat && wo.cliente_lng) return `https://www.google.com/maps/dir/?api=1&destination=${wo.cliente_lat},${wo.cliente_lng}&travelmode=driving`
                    const addr = [wo.cliente_indirizzo, wo.cliente_comune, wo.cliente_cap].filter(Boolean).join(', ')
                    if (!addr) return null
                    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}&travelmode=driving`
                  }
                  const tuttoIlPercorso = (() => {
                    const pts = pratiche.map((wo: any) => [wo.cliente_indirizzo, wo.cliente_comune].filter(Boolean).join(', ')).filter(Boolean)
                    if (pts.length === 0) return null
                    const dest = encodeURIComponent(pts[pts.length - 1])
                    const wps = pts.slice(0, -1).map(encodeURIComponent).join('|')
                    const base = `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`
                    return wps ? `${base}&waypoints=${wps}` : base
                  })()
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#fffbeb', borderRadius: 8, border: '1px solid #fde68a' }}>
                        <AlertTriangle size={14} color="#d97706" />
                        <span style={{ fontSize: 12, color: '#92400e' }}>GPS non disponibile — distanze non calcolabili. Apri Maps per la navigazione.</span>
                      </div>
                      {pratiche.map((wo: any, i: number) => {
                        const url = mapsAddr(wo)
                        const addr = [wo.cliente_indirizzo, wo.cliente_comune].filter(Boolean).join(', ')
                        return (
                          <div key={wo.id} style={{ borderRadius: 12, border: '1.5px solid #e2e8f0', background: '#f8fafc', overflow: 'hidden' }}>
                            <div style={{ padding: '9px 10px', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                              <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#1E40AF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: '#fff', flexShrink: 0 }}>{i + 1}</div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: '#1E40AF' }}>{wo.numero_progressivo}</div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: '#111827', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{wo.denominazione_sito || wo.cliente_comune || '—'}</div>
                                {addr && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{addr}</div>}
                              </div>
                              {url && (
                                <a href={url} target="_blank" rel="noopener noreferrer" style={{ flexShrink: 0, padding: '5px 10px', background: '#1E40AF', color: '#fff', borderRadius: 7, fontSize: 11, fontWeight: 700, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <Navigation size={11} /> Maps
                                </a>
                              )}
                            </div>
                          </div>
                        )
                      })}
                      {tuttoIlPercorso && (
                        <a href={tuttoIlPercorso} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px', background: '#1E40AF', color: '#fff', borderRadius: 10, fontSize: 14, fontWeight: 700, textDecoration: 'none', marginTop: 4 }}>
                          <Navigation size={15} /> Apri percorso completo in Maps
                        </a>
                      )}
                    </div>
                  )
                })()}

                {/* Percorso calcolato */}
                {percorsoStatus === 'ready' && percorsoGps && (() => {
                  const ottimaleOrdinato = ottimizzaOrdine(percorsoGps, pratiche)
                  const attualeOrdinato: any[] = [...pratiche]
                  const isOttimale = percorsoTab === 'ottimale'
                  const orderedWos = isOttimale ? ottimaleOrdinato : attualeOrdinato
                  const { steps, ritorno, totKm } = calcolaSteps(percorsoGps, orderedWos)
                  const { totKm: totKmAttuale } = calcolaSteps(percorsoGps, attualeOrdinato)
                  const { totKm: totKmOttimale } = calcolaSteps(percorsoGps, ottimaleOrdinato)
                  const risparmioKm = totKmAttuale - totKmOttimale
                  const mapUrl = mapsUrl(percorsoGps, orderedWos)
                  const warnCount = steps.filter((s: any) => s.chiusura_ok === false).length
                  const conCoordinate = steps.filter((s: any) => s.distanza_km != null).length

                  // Mappa wo_id → posizione nell'ordine attuale
                  const posizioneAttuale: Record<number, number> = {}
                  attualeOrdinato.forEach((w: any, i: number) => { posizioneAttuale[w.id] = i + 1 })

                  const RenderSteps = () => (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {steps.map((step: any, i: number) => {
                        const { wo, distanza_km, tempo_viaggio_min, ora_arrivo, ora_fine, chiusura_ok, margine_min } = step
                        const isTerminato = wo.stato === 'eseguito' || wo.stato === 'annullato'
                        const posAttuale = posizioneAttuale[wo.id]
                        const posNuova = i + 1
                        const cambiato = isOttimale && posAttuale !== posNuova
                        let chiusuraBg = '#f8fafc'; let chiusuraBorder = '#e2e8f0'; let chiusuraColor = '#6b7280'
                        if (chiusura_ok === false) { chiusuraBg = '#fef2f2'; chiusuraBorder = '#fca5a5'; chiusuraColor = '#dc2626' }
                        else if (chiusura_ok === true && margine_min != null && margine_min < 30) { chiusuraBg = '#fffbeb'; chiusuraBorder = '#fcd34d'; chiusuraColor = '#d97706' }
                        else if (chiusura_ok === true) { chiusuraBg = '#f0fdf4'; chiusuraBorder = '#bbf7d0'; chiusuraColor = '#16a34a' }
                        return (
                          <div key={wo.id} style={{ borderRadius: 12, border: `1.5px solid ${cambiato ? '#c7d2fe' : chiusuraBorder}`, background: cambiato ? '#eef2ff' : chiusuraBg, overflow: 'hidden', opacity: isTerminato ? 0.6 : 1 }}>
                            {distanza_km != null
                              ? <div style={{ padding: '4px 10px', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 5 }}>
                                  <Navigation size={10} color="#94a3b8" />
                                  <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{distanza_km.toFixed(1)} km · ~{tempo_viaggio_min} min</span>
                                </div>
                              : <div style={{ padding: '4px 10px', background: '#fef9c3', borderBottom: '1px solid #fde68a' }}>
                                  <span style={{ fontSize: 10, color: '#a16207' }}>⚠ Nessuna coordinata GPS</span>
                                </div>
                            }
                            <div style={{ padding: '9px 10px', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                                <div style={{ width: 26, height: 26, borderRadius: '50%', background: cambiato ? '#4f46e5' : '#1E40AF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: '#fff' }}>{posNuova}</div>
                                {cambiato && <span style={{ fontSize: 9, color: '#9ca3af', fontWeight: 600 }}>era {posAttuale}</span>}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: '#1E40AF' }}>{wo.numero_progressivo}</div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: '#111827', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {wo.denominazione_sito || wo.cliente_comune || '—'}
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 5 }}>
                                  <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 5, background: '#eff6ff', color: '#1d4ed8', fontWeight: 600 }}>▶ {fmtHM(ora_arrivo)}</span>
                                  <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 5, background: '#f3f4f6', color: '#374151', fontWeight: 600 }}>⏹ {fmtHM(ora_fine)}</span>
                                  {wo.ora_chiusura && (
                                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 5, background: chiusuraBg, color: chiusuraColor, fontWeight: 700, border: `1px solid ${chiusuraBorder}`, display: 'flex', alignItems: 'center', gap: 2 }}>
                                      {chiusura_ok === false ? <><AlertTriangle size={9} /> {wo.ora_chiusura} IN RITARDO</>
                                        : margine_min != null && margine_min < 30 ? <><AlertTriangle size={9} /> {wo.ora_chiusura} {margine_min}min</>
                                        : <><CheckCircle size={9} /> {wo.ora_chiusura} OK</>}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                      {ritorno && !percorsoGpsFallback && (
                        <div style={{ padding: '4px 10px', background: '#f1f5f9', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                          <Home size={10} color="#94a3b8" />
                          <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>Ritorno · {ritorno.distanza_km.toFixed(1)} km · ~{ritorno.tempo_viaggio_min} min · arrivo {fmtHM(ritorno.ora_arrivo)}</span>
                        </div>
                      )}
                    </div>
                  )

                  return (
                    <div>
                      {/* Punto partenza */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#f0fdf4', borderRadius: 10, marginBottom: 12, border: '1px solid #bbf7d0' }}>
                        <MapPin size={14} color="#16a34a" style={{ flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: percorsoGpsFallback ? '#b45309' : '#15803d' }}>
                            {percorsoGpsFallback ? 'Posizione media interventi (GPS non disp.)' : 'Partenza GPS'}
                          </div>
                          <div style={{ fontSize: 10, color: '#6b7280', fontFamily: 'monospace' }}>{percorsoGps.lat.toFixed(5)}, {percorsoGps.lng.toFixed(5)}</div>
                        </div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', textAlign: 'right' }}>
                          {totKm.toFixed(1)} km tot.
                        </div>
                      </div>

                      {/* Tab attuale / ottimale */}
                      <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 10, padding: 3, marginBottom: 12, gap: 3 }}>
                        {(['attuale', 'ottimale'] as const).map(tab => (
                          <button key={tab} onClick={() => setPercorsoTab(tab)} style={{
                            flex: 1, padding: '7px 0', borderRadius: 8, border: 'none', cursor: 'pointer',
                            fontWeight: 700, fontSize: 12, transition: 'all 0.15s',
                            background: percorsoTab === tab ? '#fff' : 'transparent',
                            color: percorsoTab === tab ? '#1E40AF' : '#6b7280',
                            boxShadow: percorsoTab === tab ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
                          }}>
                            {tab === 'attuale' ? '📋 Giro attuale' : '✨ Giro ottimale'}
                            {tab === 'ottimale' && risparmioKm > 0.5 && (
                              <span style={{ marginLeft: 4, fontSize: 10, color: '#16a34a', fontWeight: 800 }}>-{risparmioKm.toFixed(1)}km</span>
                            )}
                          </button>
                        ))}
                      </div>

                      {/* Avviso chiusura */}
                      {warnCount > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 10px', background: '#fef3c7', borderRadius: 9, marginBottom: 10, border: '1px solid #fcd34d' }}>
                          <AlertTriangle size={14} color="#d97706" />
                          <span style={{ fontSize: 11, fontWeight: 600, color: '#92400e' }}>{warnCount} pratica{warnCount > 1 ? 'he' : ''} a rischio chiusura</span>
                        </div>
                      )}

                      <RenderSteps />

                      {conCoordinate < steps.length && (
                        <div style={{ marginTop: 10, padding: '6px 10px', background: '#fef9c3', borderRadius: 8, fontSize: 11, color: '#92400e' }}>
                          ⚠ {steps.length - conCoordinate} pratica/e senza coordinate GPS
                        </div>
                      )}

                      {/* Sezione ottimale: proposta + google maps */}
                      {isOttimale && (
                        <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {proponiStatus === 'sent' ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px', background: '#f0fdf4', borderRadius: 12, border: '1px solid #bbf7d0' }}>
                              <CheckCircle size={18} color="#16a34a" />
                              <span style={{ fontSize: 13, fontWeight: 700, color: '#15803d' }}>Proposta inviata all'admin!</span>
                            </div>
                          ) : (
                            <>
                              <textarea
                                value={notaTecnico}
                                onChange={e => setNotaTecnico(e.target.value)}
                                placeholder="Nota per l'admin (opzionale)..."
                                rows={2}
                                style={{ width: '100%', borderRadius: 8, border: '1px solid #d1d5db', padding: '7px 9px', fontSize: 12, resize: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                              />
                              <button
                                onClick={() => proponiRiordino(ottimaleOrdinato)}
                                disabled={proponiStatus === 'sending'}
                                style={{
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                  padding: '13px 0', borderRadius: 12, border: 'none', cursor: 'pointer',
                                  background: '#4f46e5', color: '#fff', fontWeight: 700, fontSize: 14,
                                  opacity: proponiStatus === 'sending' ? 0.7 : 1,
                                }}
                              >
                                <Navigation size={16} />
                                {proponiStatus === 'sending' ? 'Invio...' : 'Proponi cambio ordine all\'admin'}
                              </button>
                            </>
                          )}
                          {mapUrl && (
                            <a href={mapUrl} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '11px 0', borderRadius: 12, background: '#1E40AF', color: '#fff', fontWeight: 700, fontSize: 13, textDecoration: 'none' }}>
                              <ExternalLink size={14} /> Apri in Google Maps
                            </a>
                          )}
                        </div>
                      )}

                      {!isOttimale && mapUrl && (
                        <a href={mapUrl} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, marginTop: 12, padding: '11px 0', borderRadius: 12, background: '#1E40AF', color: '#fff', fontWeight: 700, fontSize: 13, textDecoration: 'none' }}>
                          <ExternalLink size={14} /> Apri in Google Maps
                        </a>
                      )}
                    </div>
                  )
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Footer fisso ───────────────────────────────────────── */}
      <footer style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 50,
        background: '#fff',
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
      }}>
        <Link to="/pratiche" style={{
          flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 4, padding: '10px 0', textDecoration: 'none',
          color: '#6b7280', fontSize: 11, fontWeight: 600,
          minHeight: 56,
        }}>
          <ClipboardList size={20} />
          Pratiche
        </Link>
        <div style={{ width: 1, background: '#f3f4f6', margin: '8px 0' }} />
        <Link to="/mio-turno" style={{
          flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 4, padding: '10px 0', textDecoration: 'none',
          color: '#1E40AF', fontSize: 11, fontWeight: 700,
          minHeight: 56,
        }}>
          <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#1E40AF', marginBottom: -2 }} />
          Il Mio Turno
        </Link>
      </footer>
    </div>
  )
}
